import { Organisation } from "@inrange/building-manager-api-client/models-organisation";
import {
  AllowStringForNumbers,
  RecursivePartial,
  Site,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import { SiteCalculationsProvider } from "@inrange/shared-components";
import { Modal, ModalView } from "@inrange/theme-components";
import { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { applySiteUpdate } from "../site/site-utils";
import SiteInformation from "../site/siteSections/SiteInformation/SiteInformation";

const BulkEditSiteInformation = ({
  organisation,
  selectedSiteIds,
  onSitesUpdate,
  onClose,
}: {
  organisation?: Organisation;
  selectedSiteIds: Set<string>;
  onSitesUpdate: (payload: any) => void;
  onClose: () => void;
}) => {
  const [saveNote, setSaveNote] = useState("");
  const [site, setSite] = useState<
    RecursivePartial<
      AllowStringForNumbers<{
        id: string;
      }>
    >
  >({
    id: "bulk-preview",
  });
  // NOTE: newOwnerships is a weird structure - it's an object with integer index keys,
  // so it's *almost* an array, but not quite
  const [newOwnerships, setNewOwnerships] = useState({});
  const [editedOwnerships, onOwnerEdited] = useState({});
  const [deletedOwnerships, setDeletedOwnerships] = useState<
    { orgID: string; ownership: string }[]
  >([]);
  const [previewOwnerships, setPreviewOwnerships] = useState<
    { orgID: string; name: string; ownership: string }[]
  >(
    organisation
      ? [
          {
            orgID: organisation.id,
            name: organisation.name,
            ownership: "existing",
          },
        ]
      : []
  );

  const onOwnerAdded = ({
    ownershipIndex,
    selectedID,
    selectedName,
    selectedType,
    selectedPark,
  }: {
    ownershipIndex: number;
    selectedID: string;
    selectedName: string;
    selectedType: string;
    selectedPark: string;
  }) => {
    if (selectedType === "" || !selectedID) return;
    const updatedNewOwnerships: Record<
      number,
      { orgID: string; name: string; ownership: string; park?: string }
    > = {
      ...newOwnerships,
      [ownershipIndex]: {
        orgID: selectedID,
        name: selectedName,
        ownership: selectedType,
        ...(selectedPark ? { park: selectedPark } : {}),
      },
    };
    setNewOwnerships(updatedNewOwnerships);

    // Add to preview site
    const updatedSiteOwnerships = Object.values(updatedNewOwnerships).map(
      (ownership) => ({
        orgID: ownership.orgID,
        name: ownership.name,
        ownership: ownership.ownership,
        siteID: "unused",
        ...(ownership.park ? { park: ownership.park } : {}),
      })
    );
    setPreviewOwnerships(updatedSiteOwnerships);
  };

  const onOwnerRemoved = (ownership: { orgID: string; ownership: string }) => {
    // We don't support removing added but unsaved ownerships
    onOwnerEdited((prevEditedOwnerships) => {
      const updatedEditedOwnerships = { ...prevEditedOwnerships };
      delete updatedEditedOwnerships[ownership.orgID];
      return updatedEditedOwnerships;
    });
    setDeletedOwnerships([...deletedOwnerships, ownership]);
    // Remove from preview site
    const updatedOwnerships = previewOwnerships.filter(
      (o) => o.orgID !== ownership.orgID
    );
    setPreviewOwnerships(updatedOwnerships);
  };

  return (
    <Modal>
      <ModalView
        id="bulk-edit-site-information"
        title={`Edit Site Information for ${selectedSiteIds.size} site${selectedSiteIds.size === 1 ? "" : "s"}`}
        fontWeight="500"
        fontSize="20px"
        width="800px"
        titlePadding="0 0 10px 0"
      >
        <SiteCalculationsProvider
          site={site as unknown as Partial<Site>}
          originalSite={{}}
          app="admin"
          isBulkEdit={true}
        >
          <SiteInformation
            site={site as unknown as SiteAllowStringValues}
            setSite={(update) => {
              setSite((prevSite) => applySiteUpdate(prevSite, update));
            }}
            offtakerOnlySite={false}
            ownerships={previewOwnerships}
            onOwnerAdded={onOwnerAdded}
            onOwnerRemoved={onOwnerRemoved}
            onOwnerEdited={onOwnerEdited}
            setNewSiteName={() => {}}
            allowCopy={false}
            isBulkEdit={true}
            setAlertStyle={() => {}}
            setAlertMessage={() => {}}
          />
          <Form.Control
            className="mt-3"
            as="textarea"
            rows={2}
            placeholder="Add a short note describing the change made"
            value={saveNote}
            onChange={(e) => setSaveNote(e.target.value)}
            autoFocus
            data-testid="site-save-notes"
          />
          <div
            style={{
              marginTop: "16px",
              display: "flex",
              flexDirection: "row",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Button variant="inrangesecondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="success"
              onClick={() => {
                const updates = Array.from(selectedSiteIds).reduce(
                  (acc, siteId) => {
                    acc[siteId] = {
                      site,
                      newOwnerships: [
                        ...Object.values(newOwnerships),
                        ...Object.values(editedOwnerships),
                      ],
                      deletedOwnerships,
                    };
                    if (saveNote.trim().length > 0) {
                      acc[siteId].userNote = saveNote.trim();
                    }
                    return acc;
                  },
                  {} as { [key: string]: any }
                );
                onSitesUpdate(updates);
              }}
            >
              Edit Site information
            </Button>
          </div>
        </SiteCalculationsProvider>
      </ModalView>
    </Modal>
  );
};

export default BulkEditSiteInformation;
